import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Mail, ChevronDown, ChevronUp } from 'lucide-react'

interface StepAccountCreateProps {
  onComplete: () => void
}

export function StepAccountCreate({ onComplete }: StepAccountCreateProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [otpType, setOtpType] = useState<'email' | 'signup'>('email')

  const otpInputRef = useRef<HTMLInputElement>(null)

  const { signUp, signUpWithOtp, verifyOtp } = useAuth()
  const { toast } = useToast()

  // Fire-and-forget welcome email after account creation
  const sendWelcomeEmail = () => {
    supabase.functions.invoke('send-welcome-email').catch((err) => {
      console.error('Failed to send welcome email:', err)
    })
  }

  // Focus OTP input when it appears
  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [otpSent])

  // Primary: Send OTP code
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter your name.',
        variant: 'destructive',
      })
      return
    }

    setLoading(true)

    const redirectTo = `${window.location.origin}/register`
    const { error } = await signUpWithOtp(email, name.trim(), redirectTo)

    if (error) {
      toast({
        title: 'Failed to send code',
        description: error.message,
        variant: 'destructive',
      })
      setLoading(false)
      return
    }

    setOtpSent(true)
    setLoading(false)
    toast({
      title: 'Code sent!',
      description: 'Check your email for a 6-digit code.',
    })
  }

  // Verify OTP code
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otpCode || otpCode.length !== 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter the 6-digit code from your email.',
        variant: 'destructive',
      })
      return
    }

    setLoading(true)
    const { error } = await verifyOtp(email, otpCode, otpType)

    if (error) {
      toast({
        title: 'Verification failed',
        description: error.message,
        variant: 'destructive',
      })
      setLoading(false)
      return
    }

    sendWelcomeEmail()
    toast({ title: 'Account created!' })
    onComplete()
  }

  // Resend OTP code
  const handleResendOtp = async () => {
    setLoading(true)
    try {
      const redirectTo = `${window.location.origin}/register`
      const { error } = await signUpWithOtp(email, name.trim(), redirectTo)

      if (error) {
        toast({
          title: 'Failed to resend',
          description: error.message,
          variant: 'destructive',
        })
        return
      }

      // Resend uses signUpWithOtp which generates an 'email' type token
      setOtpType('email')
      toast({
        title: 'Code resent!',
        description: 'Check your email for a new code.',
      })
    } finally {
      setLoading(false)
    }
  }

  // Fallback: Password signup
  const handlePasswordSignup = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter your name.',
        variant: 'destructive',
      })
      return
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match.',
        variant: 'destructive',
      })
      return
    }

    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      })
      return
    }

    setLoading(true)

    const redirectTo = `${window.location.origin}/register`
    const { error, session } = await signUp(email, password, name.trim(), redirectTo)

    if (error) {
      toast({
        title: 'Registration failed',
        description: error.message,
        variant: 'destructive',
      })
      setLoading(false)
      return
    }

    if (session) {
      sendWelcomeEmail()
      toast({ title: 'Account created!' })
      onComplete()
      return
    }

    // Email confirmation required — show OTP screen so they can enter the code
    // Password signups generate a 'signup' type token, not an 'email' type
    setOtpType('signup')
    setOtpSent(true)
    setLoading(false)
  }

  // OTP verification screen
  if (otpSent) {
    return (
      <div>
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-full">
              <Mail className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-2">Check your email</h2>
          <p className="text-zinc-600 dark:text-zinc-400 text-sm">
            We sent a 6-digit code to <strong>{email}</strong>.
            Enter it below or click the link in your email.
          </p>
        </div>

        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="otp-code">Verification code</Label>
            <Input
              ref={otpInputRef}
              id="otp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              autoComplete="one-time-code"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading || otpCode.length !== 6}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </Button>
        </form>

        <div className="mt-4 space-y-2">
          <Button
            variant="ghost"
            onClick={handleResendOtp}
            disabled={loading}
            className="w-full text-sm"
          >
            Didn't get the code? Resend
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setOtpSent(false)
              setOtpCode('')
            }}
            className="w-full text-sm"
          >
            Use a different email
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Create your account</h2>
      <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
        First, let's set up your Socrates account.
      </p>

      {/* Primary: OTP signup */}
      <form onSubmit={handleSendOtp} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reg-name">Full Name</Label>
          <Input
            id="reg-name"
            type="text"
            placeholder="John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-email">Email</Label>
          <Input
            id="reg-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>

        <Button type="submit" className="w-full" disabled={loading || !name.trim() || !email}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending code...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Send me a verification code
            </>
          )}
        </Button>
      </form>

      {/* Secondary: Password option */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="flex items-center justify-center gap-1 w-full text-sm text-zinc-500 dark:text-zinc-400
                     hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
        >
          {showPassword ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          I'd rather use a password
        </button>

        {showPassword && (
          <form onSubmit={handlePasswordSignup} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reg-password">Password</Label>
              <Input
                id="reg-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
              />
              <p className="text-xs text-zinc-500">Must be at least 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg-confirm">Confirm Password</Label>
              <Input
                id="reg-confirm"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account & continue'
              )}
            </Button>
          </form>
        )}
      </div>

      <p className="mt-6 text-center text-sm text-zinc-600 dark:text-zinc-400">
        Already have an account?{' '}
        <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="font-medium text-zinc-800 dark:text-zinc-200 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}
